import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'

import en from './languages/en'
import sv from './languages/sv'

export const translations = { en, sv } as const

const resources = Object.entries(translations).reduce(
  (acc, [key, value]) => ({ ...acc, [key]: { translation: value } }),
  {},
)

export type AvailableLocale = keyof typeof translations

export const availableLocales = Object.keys(translations) as AvailableLocale[]

export const fallbackLng = 'sv'

/**
 * @description Used for looking for missing translations and breaking UI in development
 */
export const enablePizzaDebugLanguage = (length = 20) => {
  if (import.meta.env.VITE_DEV !== 'true') return
  // @ts-expect-error linter migration
  i18n.t = () => Array.from({ length }, () => '🍕').join('')
}

/* 
  TODO: Fix type safety for translation keys
  https://react.i18next.com/latest/typescript
  https://github.com/i18next/react-i18next/issues/1568
*/

/**
 * Keeps `<html lang>` on the language actually being rendered.
 *
 * It was left at the "en" in index.html forever, while almost everything the app shows is
 * Swedish. Two consequences, and the second is the one users report:
 *
 * A screen reader trusts this attribute to choose pronunciation, so Swedish was being read
 * with English phonetics — which matters more here than in most products.
 *
 * And a browser that finds the content disagreeing with the declared language offers to
 * translate the page. Accepting that hands the DOM to Google Translate, which swaps text
 * nodes for its own copies; React still holds the originals, and the next removal throws
 * "Failed to execute 'removeChild' on 'Node'" from deep inside the commit phase, with no
 * frame of ours in the stack. Declaring the language honestly removes the prompt.
 */
const syncDocumentLanguage = (language: string) => {
  if (typeof document === 'undefined') return

  document.documentElement.lang = language
}

export const initI18n = () => {
  i18n.on('languageChanged', syncDocumentLanguage)

  i18n
    .use(initReactI18next)
    .use(LanguageDetector)
    .init({
      detection: {
        caches: [], // Disable caching — locale is managed via Redux
        order: ['navigator'], // Only use browser language for initial detection
      },
      fallbackLng,
      interpolation: {
        escapeValue: false,
      },
      load: 'languageOnly', // Strips region code: sv-SE → sv
      resources,
      supportedLngs: availableLocales,
    })

  // `languageChanged` does not fire for the language init settles on, so the first value
  // has to be taken directly.
  syncDocumentLanguage(i18n.resolvedLanguage ?? fallbackLng)
}

export default i18n
